/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { PlayerState } from "../../../videoplayer/enums";
import type { TSnapshot } from "../types";

const BUFFER_PERCENT_MIN = 0;
const BUFFER_PERCENT_MAX = 100;
const PERCENT_MULTIPLIER = 100;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/**
 * Maps a Web `PlayerState` onto the spec's coarse four-way `streaming_state`
 * vocabulary (§6.2). States with no active-stream meaning are omitted
 * rather than forced into a bucket.
 */
export const mapPlayerStateToStreamingState = (
  state: PlayerState | null
): string | undefined => {
  if (state === null) {
    return undefined;
  }
  switch (state) {
    case PlayerState.LOADING:
    case PlayerState.READY:
    case PlayerState.INITIATION:
    case PlayerState.DRM_CONFIGURED:
      return "preflight";
    case PlayerState.BUFFERING:
      return "videoStarting";
    case PlayerState.PLAYING:
    case PlayerState.PAUSED:
    case PlayerState.SEEKING:
    case PlayerState.FIRST_FRAME:
      return "videoStreaming";
    default:
      return undefined;
  }
};

/**
 * `video_buffer_available` (`vba`) per spec §6.4 — clamped 0-100, omitted
 * when nothing is buffered or duration is unknown (e.g. live).
 */
export const computeVideoBufferAvailablePercent = (
  bufferedInfo: TimeRanges | null,
  durationSec: number
): number | undefined => {
  if (bufferedInfo === null || bufferedInfo.length === 0) {
    return undefined;
  }
  if (!Number.isFinite(durationSec) || durationSec <= 0) {
    return undefined;
  }
  const bufferedEndSec = bufferedInfo.end(bufferedInfo.length - 1);
  return clamp(
    (bufferedEndSec / durationSec) * PERCENT_MULTIPLIER,
    BUFFER_PERCENT_MIN,
    BUFFER_PERCENT_MAX
  );
};

export const buildSnapshot = (
  playerState: PlayerState | null,
  positionMs: number,
  bufferedInfo: TimeRanges | null,
  durationSec: number,
  ccLanguage: string | undefined
): TSnapshot => {
  return {
    playPositionMs: positionMs,
    streamingState: mapPlayerStateToStreamingState(playerState),
    videoBufferAvailablePercent: computeVideoBufferAvailablePercent(
      bufferedInfo,
      durationSec
    ),
    ccLanguage
  };
};
